/**
 * Máquina de estados de topo do player.
 *
 * States:
 *   initializing → verifica credenciais no store
 *   pairing      → sem credenciais — aguarda confirmação no painel web
 *   starting     → credenciais ok — carrega cache + primeiro sync
 *   playing      → loop de reprodução ativo
 *   standby      → fora do horário de operação do device (heartbeat.operatingHoursAction ≠ 'none'): tela preta
 *   error        → erro crítico irrecuperável
 *
 * workspaceId/workspaceName são populados pelo primeiro heartbeat bem-sucedido.
 */
import { useEffect } from 'react'
import { scheduleManager } from '@/engine/schedule-manager'
import { ErrorScreen } from '@/screens/error-screen'
import { FallbackScreen } from '@/screens/fallback-screen'
import { PairingScreen } from '@/screens/pairing-screen'
import { PlayerScreen } from '@/screens/player-screen'
import { StandbyScreen } from '@/screens/standby-screen'
import { useAuthStore } from '@/store/auth.store'
import { usePlayerStore } from '@/store/player.store'
import { playerLogger } from '@/utils/player-logger'

export function App() {
  const screen = usePlayerStore((s) => s.screen)
  const setScreen = usePlayerStore((s) => s.setScreen)
  const credentials = useAuthStore((s) => s.credentials)

  useEffect(() => {
    if (screen !== 'initializing') return

    if (!credentials?.deviceId || !credentials?.deviceToken) {
      setScreen('pairing')
      return
    }

    scheduleManager
      .loadFromCache()
      .then((cache) => {
        setScreen(cache && cache.slots.length > 0 ? 'playing' : 'starting')
        playerLogger.debug('[App] Loaded schedule from cache, slots:', cache?.slots.length ?? 0)
      })
      .catch(() => {
        // Se o cache falhar (DB corrompido, etc), trata como sem cache
        setScreen('starting')
      })
  }, [screen, credentials, setScreen])

  if (screen === 'initializing') {
    return <FallbackScreen />
  }

  if (screen === 'pairing') {
    return <PairingScreen onPaired={() => setScreen('starting')} />
  }

  if (screen === 'standby') {
    return <StandbyScreen />
  }

  if (screen === 'error') {
    return <ErrorScreen />
  }

  return <PlayerScreen />
}
